import { useReactiveVar } from "@apollo/client";
import { useDroppable } from "@dnd-kit/core";
import { useCallback, useMemo, useState } from "react";

import {
  DEFAULT_FILTER_STATE,
  type FilterState,
} from "@/features/player-kanban/constants/filterDefaults";
import usePlayerQuery from "@/features/player-kanban/hooks/usePlayerQuery";
import useSidebarInteraction from "@/features/player-kanban/hooks/useSidebarInteraction";
import useSidebarState from "@/features/player-kanban/hooks/useSidebarState";
import useWatchlistQuery from "@/features/player-kanban/hooks/useWatchlistQuery";
import type { RemovePlayerHandler } from "@/features/player-kanban/types/types";
import type { ServerGpvRange } from "@/features/player-kanban/utils/playerFilter";
import { PlayerSource } from "@/features/player-kanban/components/molecules/PlayerSourceSelector";
import PlayerSidebarView from "./PlayerSidebarView";

import { MY_ROSTER_KANBAN } from "@/config/featureFlags";
import { myRosterSelectedVar } from "@/stores/watchlistSelection";

/**
 * Props for PlayerSidebarContainer component
 */
interface PlayerSidebarContainerProps {
  /**
   * Whether any item is currently being dragged globally
   */
  isGloballyDragging?: boolean;
  /**
   * The source of the current drag operation
   */
  dragSource?: "sidebar" | "kanban" | null;
  /**
   * Array of kanban card objects containing player and phase information
   */
  kanbanCards?: {
    /**
     * Player object with ID
     */
    player: { id: string };
    /**
     * Kanban phase object with ID and name
     */
    kanbanPhase: { id: string; name: string };
  }[];
  /**
   * Callback invoked when any drag operation starts
   */
  onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  /**
   * Callback invoked when any drag operation ends
   */
  onGlobalDragEnd?: () => void;
  /**
   * Callback invoked when a player is dropped in the removal zone
   */
  onRemovePlayerFromBoard?: RemovePlayerHandler;
  /**
   * Callback invoked to trigger automatic scrolling during drag operations
   */
  onAutoScroll?: (dragX: number) => void;
  /**
   * Active board filters, applied to the unplaced (sidebar) player pool.
   */
  boardFilters?: FilterState;
  /**
   * Active GPV bounds (euros) for the server-side watchlist filter (SE-6496).
   * Null while the cost filter is inactive.
   */
  serverGpvRange?: ServerGpvRange | null;
}

/**
 * Container component for the player sidebar that orchestrates player source selection, watchlist management, and drag-drop interactions.
 * @param root0 - The component props
 * @param root0.isGloballyDragging - Whether any item is currently being dragged globally
 * @param root0.dragSource - The source of the current drag operation (sidebar, kanban, or null)
 * @param root0.kanbanCards - Array of kanban card objects containing player and phase information
 * @param root0.onGlobalDragStart - Callback invoked when any drag operation starts
 * @param root0.onGlobalDragEnd - Callback invoked when any drag operation ends
 * @param root0.onRemovePlayerFromBoard - Callback invoked when a player is dropped in the removal zone
 * @param root0.onAutoScroll - Callback invoked to trigger automatic scrolling during drag operations
 * @param root0.boardFilters - Active board filters applied to the unplaced (left-panel) player pool
 * @param root0.serverGpvRange - Active GPV bounds (euros) for the server-side watchlist filter (SE-6496); null when inactive
 */
const PlayerSidebarContainer = ({
  isGloballyDragging = false,
  dragSource = null,
  kanbanCards = [],
  onGlobalDragStart,
  onGlobalDragEnd,
  onRemovePlayerFromBoard,
  onAutoScroll,
  boardFilters = DEFAULT_FILTER_STATE,
  serverGpvRange = null,
}: PlayerSidebarContainerProps) => {
  const isMyRosterEnabled = useMemo(() => MY_ROSTER_KANBAN === "on", []);

  // Use reactive variable to sync with homepage pattern
  const isMyRosterSelected = useReactiveVar(myRosterSelectedVar);
  const [playerSource, setPlayerSource] = useState<PlayerSource>(
    isMyRosterEnabled && isMyRosterSelected ? "roster" : "watchlist"
  );

  const handleSourceChange = useCallback(
    (source: PlayerSource) => {
      // Prevent switching to roster if feature flag is off
      if (source === "roster" && !isMyRosterEnabled) {
        return;
      }
      setPlayerSource(source);
      myRosterSelectedVar(source === "roster");
    },
    [isMyRosterEnabled]
  );

  // Initialize focused hooks
  const watchlistQuery = useWatchlistQuery();

  const playerQuery = usePlayerQuery({
    playerSource,
    watchListSelected: watchlistQuery.watchListSelected || "",
    queryWatchlistId: watchlistQuery.queryWatchlistId,
    allWatchlistIds: watchlistQuery.allWatchlistIds,
    serverGpvRange,
  });

  const sidebarState = useSidebarState({
    processedPlayers: playerQuery.processedPlayers,
    kanbanCards,
    boardFilters,
    contractValueFilteredServerSide:
      playerSource === "watchlist" && serverGpvRange != null,
  });

  const sidebarInteraction = useSidebarInteraction({
    onGlobalDragStart,
    onGlobalDragEnd,
    onRemovePlayerFromBoard,
    onAutoScroll,
  });

  // Compute whether to show removal zone
  const showRemovalZone = useMemo(
    () => isGloballyDragging && dragSource === "kanban",
    [isGloballyDragging, dragSource]
  );

  // Set up droppable with @dnd-kit for removal zone
  const { setNodeRef, isOver } = useDroppable({
    id: "sidebar-removal-zone",
    data: {
      type: "removal",
    },
    disabled: !showRemovalZone,
  });

  // Combine style classes based on drop state
  const sidebarClassName = useMemo<string>(() => {
    const background =
      showRemovalZone && isOver
        ? "bg-status-error/50"
        : showRemovalZone
          ? "bg-status-error/30"
          : "bg-surface-base";
    const border =
      showRemovalZone && isOver
        ? "border-2 border-solid border-status-error"
        : "border-0 border-solid border-transparent";
    const position = isGloballyDragging ? "relative" : "";
    return `h-full w-80 overflow-visible rounded-none ${background} ${border} ${position}`.trim();
  }, [showRemovalZone, isOver, isGloballyDragging]);

  return (
    <PlayerSidebarView
      stats={sidebarState.stats}
      players={sidebarState.filteredPlayers}
      searchQuery={sidebarState.searchQuery}
      sortLabel={sidebarState.sortLabel}
      playerSource={playerSource}
      selectedWatchlist={watchlistQuery.selectedWatchlist}
      watchlists={watchlistQuery.watchlists}
      playersOnKanban={sidebarState.playersOnKanban}
      isLoadingWatchlists={watchlistQuery.watchlistsLoading}
      watchlistError={watchlistQuery.watchlistsError?.message || null}
      isLoadingPlayers={playerQuery.finalPlayersLoading}
      playersError={playerQuery.finalPlayersError?.message || null}
      isGloballyDragging={isGloballyDragging}
      dragSource={dragSource}
      showRemovalZone={showRemovalZone}
      sidebarClassName={sidebarClassName}
      setNodeRef={setNodeRef}
      isMyRosterEnabled={isMyRosterEnabled}
      onSearchChange={sidebarState.handleSearchChange}
      onSortChange={sidebarState.handleSortChange}
      onPlayerClick={sidebarInteraction.handlePlayerClick}
      onSourceChange={handleSourceChange}
      onWatchlistChange={watchlistQuery.handleWatchlistChange}
      onGlobalDragStart={sidebarInteraction.handleGlobalDragStart}
      onGlobalDragEnd={sidebarInteraction.handleGlobalDragEnd}
      onRemovePlayerFromBoard={sidebarInteraction.handleRemovePlayerFromBoard}
      onAutoScroll={sidebarInteraction.handleAutoScroll}
      onLoadMore={playerQuery.loadMore}
    />
  );
};

export default PlayerSidebarContainer;
